import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Translate } from '../../../../../../../shared/LanguageService';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { IOrderRow } from '../../../Entities';
import { useImageCompare } from '../../../shared/compareImages/state/use-image-compare';
import { canCompareImages } from '../../../shared/compareImages/state/utils';
import { CompareOverlay } from '../../../shared/compareImages/view/CompareOverlay';
import { ImageCard } from '../../../shared/imageCard/view/ImageCard';
import { ImagePlaceholderContent } from '../../../shared/imageCard/view/ImagePlaceholderContent';
import { useImageSelection } from '../../../shared/selection/state/use-image-selection';
import { useIsShiftPressed } from '../../../shared/selection/state/use-is-shift-pressed';
import { useCompareShortcut } from '../../compareImages/state/use-compare-shortcut';
import { canDownloadUneditedImages } from '../../selectImages/state/utils';
import {
    SelectImagesToolbar,
    SelectImagesVariant
} from '../../selectImages/view/SelectImagesToolbar';
import {
    ImageEditorPhotoStatus,
    OrderRowQuantityInfo
} from '../../entry/queries/use-query-photo-status';
import { ImageCommentsDialog } from '../../imageComments/view/ImageCommentsDialog';
import EditedImagePreviewDialog from '../../imagePreview/view/EditedImagePreviewDialog';
import { UploadEditedImagesSection } from '../../uploadEditedImages/view/UploadEditedImagesSection';
import {
    getImagesInProgress,
    getPendingSkeletonCount,
    PendingUploads,
    removePendingUploads
} from '../state/order-row-summary';
import { getThumbnailActionIcons } from '../state/thumbnail-action-icons';
import { ImageInProgressCard } from './ImageInProgressCard';
import { OrderRowHeader } from './OrderRowHeader';
import { ImageGrid, ImagesBody } from './OrderRowImagesLayout';
import { ThumbnailActionIcons } from './ThumbnailActionIcons';
import { ThumbnailCard } from './ThumbnailCard';

const InProgressNotice = styled(Typography)({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '16px 16px 0',
    fontSize: '14px'
}) as typeof Typography;

const ThumbnailFrame = styled('div')({
    position: 'relative'
});

type OrderRowImagesSectionProps = {
    orderId: number;
    orderRow: IOrderRow;
    showPlaceholders: boolean;
    photoStatus: ImageEditorPhotoStatus;
    additionalProducts: OrderRowQuantityInfo[];
    orderRowsUpdatedAt: number;
    user: AuthenticatedUser;
    ownsCompareShortcut: boolean;
    onClaimCompareShortcut: () => void;
};

export function OrderRowImagesSection({
    orderId,
    orderRow,
    showPlaceholders,
    photoStatus,
    additionalProducts,
    orderRowsUpdatedAt,
    user,
    ownsCompareShortcut,
    onClaimCompareShortcut
}: OrderRowImagesSectionProps) {
    let isEditedTab = !showPlaceholders;
    let [pendingUploads, setPendingUploads] = React.useState<PendingUploads | null>(null);
    let [openImageId, setOpenImageId] = React.useState<number | null>(null);
    let selection = useImageSelection();
    let { isComparing, openCompare, closeCompare } = useImageCompare();
    useCompareShortcut({
        isEnabled:
            ownsCompareShortcut &&
            isEditedTab &&
            selection.isSelecting &&
            canCompareImages(selection.selectedImageIds.size),
        onCompare: openCompare
    });

    function startSelection() {
        onClaimCompareShortcut();
        selection.startSelection();
    }

    function toggleImageSelection(imageId: number) {
        onClaimCompareShortcut();
        selection.toggleImageSelection(imageId);
    }
    let isShiftPressed = useIsShiftPressed();
    let canStartSelectionByShiftClick = isEditedTab || canDownloadUneditedImages(user);
    let openThumbnail = orderRow.thumbnails.find((thumbnail) => thumbnail.id === openImageId);
    let openFilename = openThumbnail ? openThumbnail.filename : '';

    let serverImagesInProgress = getImagesInProgress(orderRow, photoStatus, isEditedTab);
    let pendingSkeletonCount = getPendingSkeletonCount(
        pendingUploads,
        orderRow,
        orderRowsUpdatedAt
    );
    let extraSkeletonCount = Math.max(0, pendingSkeletonCount - serverImagesInProgress.length);
    let imagesInProgress = serverImagesInProgress.concat(
        Array.from({ length: extraSkeletonCount }, () => ({ filename: null }))
    );

    function handleUploaded(uploadedFilenames: string[]) {
        setPendingUploads({ filenames: uploadedFilenames, uploadedAt: Date.now() });
    }

    function handleImagesDeleted(imageIds: number[]) {
        let deletedFilenames = orderRow.thumbnails
            .filter((thumbnail) => imageIds.includes(thumbnail.id))
            .map((thumbnail) => thumbnail.filename);

        setPendingUploads((current) => removePendingUploads(current, deletedFilenames));
    }
    let noticeKey = isEditedTab
        ? 'order-page/edited-image/upload-in-progress'
        : 'image-processing/header/uploads-in-progress';

    return (
        <Box component="section">
            <OrderRowHeader
                orderRow={orderRow}
                photoStatus={photoStatus}
                additionalProducts={additionalProducts}
                actions={
                    <SelectImagesToolbar
                        orderId={orderId}
                        variant={
                            isEditedTab ? SelectImagesVariant.Edited : SelectImagesVariant.Unedited
                        }
                        user={user}
                        photoStatus={photoStatus}
                        images={orderRow.thumbnails}
                        isSelecting={selection.isSelecting}
                        selectedImageIds={selection.selectedImageIds}
                        onSelectStart={startSelection}
                        onSelectAll={selection.selectAllImages}
                        onDeselectAll={selection.deselectAllImages}
                        onCancel={selection.cancelSelection}
                        onCompare={isEditedTab ? openCompare : undefined}
                        onImagesDeleted={handleImagesDeleted}
                    />
                }
            />
            <ImagesBody>
                {imagesInProgress.length > 0 && (
                    <InProgressNotice component="p" role="status">
                        {Translate(noticeKey)}
                    </InProgressNotice>
                )}
                <ImageGrid>
                    {orderRow.thumbnails.map((thumbnail) => (
                        <ThumbnailFrame key={thumbnail.id}>
                            <ThumbnailCard
                                isSelecting={selection.isSelecting}
                                isSelected={selection.selectedImageIds.has(thumbnail.id)}
                                isShiftPressed={isShiftPressed}
                                onToggleSelect={() => toggleImageSelection(thumbnail.id)}
                                onStartSelection={
                                    canStartSelectionByShiftClick ? startSelection : undefined
                                }
                                onOpen={
                                    isEditedTab || thumbnail.hasComment
                                        ? () => setOpenImageId(thumbnail.id)
                                        : undefined
                                }
                            >
                                <ImageCard
                                    imageReference={thumbnail.reference}
                                    filename={thumbnail.filename}
                                    category={thumbnail.categoryId}
                                    isPortrait={
                                        thumbnail.highResolutionWidth <
                                        thumbnail.highResolutionHeight
                                    }
                                    content={
                                        showPlaceholders ? <ImagePlaceholderContent /> : undefined
                                    }
                                    boldFilename={showPlaceholders && thumbnail.hasComment}
                                />
                            </ThumbnailCard>
                            <ThumbnailActionIcons
                                icons={getThumbnailActionIcons(thumbnail, user, photoStatus)}
                            />
                        </ThumbnailFrame>
                    ))}
                    {imagesInProgress.map((image, index) => (
                        <ImageInProgressCard
                            key={`in-progress-${image.filename || index}`}
                            filename={image.filename}
                        />
                    ))}
                </ImageGrid>
            </ImagesBody>
            {isEditedTab ? (
                <EditedImagePreviewDialog
                    orderId={orderId}
                    imageId={openImageId}
                    imageIds={orderRow.thumbnails.map((thumbnail) => thumbnail.id)}
                    filename={openFilename}
                    user={user}
                    photoStatus={photoStatus}
                    onClose={() => setOpenImageId(null)}
                    onImageReplaced={setOpenImageId}
                    onNavigate={setOpenImageId}
                />
            ) : (
                <ImageCommentsDialog
                    orderId={orderId}
                    imageId={openImageId}
                    filename={openFilename}
                    user={user}
                    onClose={() => setOpenImageId(null)}
                />
            )}
            {isEditedTab && (
                <CompareOverlay
                    open={isComparing}
                    onClose={closeCompare}
                    images={orderRow.thumbnails}
                    selectedImageIds={selection.selectedImageIds}
                />
            )}
            {isEditedTab && (
                <UploadEditedImagesSection
                    orderId={orderId}
                    orderRow={orderRow}
                    photoStatus={photoStatus}
                    user={user}
                    onUploaded={handleUploaded}
                />
            )}
        </Box>
    );
}
